import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getMeetingById, updateMeeting as apiUpdateMeeting, deleteMeeting as apiDeleteMeeting } from '../services/api';
import { useSelector, useDispatch } from 'react-redux';
import { updateMeeting, removeMeeting } from '../store/slices/meetingsSlice';
import MeetingFormModal from '../components/meetings/MeetingFormModal';
import toast from 'react-hot-toast';
import { useUiLanguage } from '../components/profile/coaching-personality/useUiLanguage';
import i18n from '../i18n';
import { getStaticTranslation } from '../translations/registerStaticTranslations';

const MeetingDetails = () => {
  const { meetingId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(state=>state.auth);
  // QAV5-502: same native-dialog gap as Meetings.js — look the sentence up in
  // the shared static bundle before handing it to window.confirm().
  const currentLanguage = useUiLanguage();
  const tr = (s) => getStaticTranslation(i18n, s, currentLanguage) || s;

  const [meeting, setMeeting] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setModalOpen] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      const res = await getMeetingById(meetingId);
      if (res.success) {
        setMeeting(res.data);
      } else {
        toast.error('Failed to load meeting');
      }
      setLoading(false);
    };
    fetchData();
  }, [meetingId]);

  const isHost = meeting?.schedule_by?.email === user?.email;

  const handleSaveEdit = async (payload) => {
    const res = await apiUpdateMeeting(meeting.id, payload);
    if (res.success) {
      setMeeting(res.data);
      dispatch(updateMeeting(res.data));
      toast.success('Meeting updated');
      setModalOpen(false);
    } else {
      toast.error('Failed to update');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm(tr('Cancel this meeting?'))) return;
    const res = await apiDeleteMeeting(meeting.id);
    if (res.success) {
      dispatch(removeMeeting(meeting.id));
      toast.success('Meeting cancelled');
      navigate('/meetings');
    } else {
      toast.error('Failed to cancel');
    }
  };

  if (loading) return <p className="p-6">Loading…</p>;
  if (!meeting) return <p className="p-6 text-red-600">Meeting not found.</p>;

  return (
    <div className="p-6 max-w-3xl mx-auto">
      <h1 className="text-2xl font-bold text-gray-900 mb-4">{meeting.topic}</h1>
      <p className="text-gray-600 mb-2">Date: {new Date(meeting.schedule_date).toLocaleString()}</p>
      <p className="text-gray-600 mb-2">Duration: {meeting.duration} min</p>
      <p className="text-gray-600 mb-2">Timezone: {meeting.timezone}</p>
      {meeting.agenda && <p className="text-gray-600 mb-6 whitespace-pre-wrap">Agenda: {meeting.agenda}</p>}

      <div className="flex gap-3 mb-6">
        <a
          href={isHost ? meeting.start_url : meeting.join_url}
          target="_blank"
          rel="noopener noreferrer"
          className="px-4 py-2 bg-indigo-600 text-white rounded-md text-sm"
        >
          {isHost ? 'Start' : 'Join'} Meeting
        </a>
        {isHost && (
          <>
            <button onClick={() => setModalOpen(true)} className="px-4 py-2 bg-gray-100 text-gray-800 rounded-md text-sm">Edit</button>
            <button onClick={handleCancel} className="px-4 py-2 bg-red-600 text-white rounded-md text-sm">Cancel</button>
          </>
        )}
      </div>

      <MeetingFormModal
        isOpen={isModalOpen}
        onClose={() => setModalOpen(false)}
        onSubmit={handleSaveEdit}
        mode="edit"
        initialData={meeting}
      />
    </div>
  );
};

export default MeetingDetails; 